/**
 * LogicWizard — modal that builds an `IF ...` premise visually, plus the
 * statements that follow it (its "body": TERMINATE / SKIP TO / a question /
 * BEGIN-END).
 *
 * Flow:
 *   1. On open, parse the questionnaire into LabelItem[] and a question-info map.
 *   2. If `initialIfText` is set, split into IF line + body lines and parse both.
 *   3. Render a recursive ConditionGroup tree + a BodyEditor.
 *   4. On Apply, call `onApply(<IF>\n<body lines, if any>)`.
 */
import React, { useMemo, useState } from "react"
import { Alert, ConfigProvider, Modal, Tooltip } from "antd"
import { CodeOutlined } from "@ant-design/icons"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import { LabelItem, LabelType, parseLabelsAndLists } from "utils/labelUtils"
import { htmlToText } from "utils/htmlTextConverter"
import {
  ASTNode,
  GroupNode,
  PlaceholderNode,
} from "utils/logicWizard/grammar"
import {
  generateIfLine,
  isASTComplete,
} from "utils/logicWizard/ifGenerator"
import { parseIfLine } from "utils/logicWizard/ifParser"
import {
  BodyAction,
  DEFAULT_BODY_ACTION,
  generateBodyLines,
  isBodyComplete,
  parseBodyLines,
} from "utils/logicWizard/bodyAction"
import {
  buildQuestionInfoMap,
  QuestionInfo,
} from "utils/logicWizard/questionTypeDetector"
import ConditionGroup from "./ConditionGroup.component"
import BodyEditor from "./BodyEditor.component"

interface LogicWizardProps {
  /** Whether the modal is visible. */
  isOpen: boolean
  /** Close without applying. */
  onClose: () => void
  /**
   * Called with the full block (IF line + any body lines, joined by `\n`)
   * when the user clicks Apply.
   */
  onApply: (text: string) => void
  /**
   * The full questionnaire HTML (or plain text). Used to derive labels and
   * question types. The wizard converts HTML→text internally if needed.
   */
  questionnaireHtml: string
  /**
   * If set, the wizard parses this text and populates the form for editing.
   * If null/undefined, starts with an empty group.
   */
  initialIfText?: string | null
}

const placeholder: PlaceholderNode = { kind: "placeholder" }

const LogicWizard: React.FC<LogicWizardProps> = ({
  isOpen,
  onClose,
  onApply,
  questionnaireHtml,
  initialIfText,
}) => {
  const plainText = useMemo(
    () => htmlToText(questionnaireHtml),
    [questionnaireHtml],
  )
  const labels = useMemo<LabelItem[]>(
    () => parseLabelsAndLists(plainText),
    [plainText],
  )
  const questionInfo = useMemo<Map<string, QuestionInfo>>(
    () => buildQuestionInfoMap(labels, plainText),
    [labels, plainText],
  )
  const listMap = useMemo<ReadonlyMap<string, LabelItem>>(() => {
    const m = new Map<string, LabelItem>()
    for (const l of labels) {
      if (l.type === LabelType.LIST || l.type === LabelType.RANGE_LIST) {
        m.set(l.name, l)
      }
    }
    return m
  }, [labels])

  // Initial state is fully derived from `initialIfText`. We use lazy useState
  // initialisers (run once per mount) instead of a useEffect that re-runs
  // setState — avoiding the React anti-pattern flagged in
  // https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
  // The parent (Editor.tsx) keys this component on `initialIfText`, so any
  // change to the prop triggers a clean remount and these initialisers run
  // again with the new value.
  const [ast, setAst] = useState<ASTNode>(() =>
    computeInitialState(initialIfText).ast,
  )
  const [bodyAction, setBodyAction] = useState<BodyAction>(
    () => computeInitialState(initialIfText).bodyAction,
  )
  const [parseErrors] = useState<string[]>(
    () => computeInitialState(initialIfText).parseErrors,
  )

  const preview = useMemo(() => {
    try {
      const ifLine = generateIfLine(ast)
      const bodyLines = generateBodyLines(bodyAction)
      return [ifLine, ...bodyLines].join("\n")
    } catch {
      return "IF …"
    }
  }, [ast, bodyAction])

  const canApply = useMemo(
    () => isASTComplete(ast) && isBodyComplete(bodyAction),
    [ast, bodyAction],
  )

  const handleApply = () => {
    if (!canApply) return
    const ifLine = generateIfLine(ast)
    const bodyLines = generateBodyLines(bodyAction)
    onApply([ifLine, ...bodyLines].join("\n"))
    onClose()
  }

  return (
    <ConfigProvider
      theme={{
        token: {
          // DataQ brand cyan — applies to AntD primary controls (radios,
          // selects, segmented, buttons, links, focus rings, etc.) so the
          // wizard matches the brand instead of AntD's default blue.
          colorPrimary: "#36C3ED",
          colorLink: "#36C3ED",
          colorLinkHover: "#2BA8CF",
          colorLinkActive: "#1F8AAB",
        },
      }}
    >
    <Modal
      open={isOpen}
      onCancel={onClose}
      width={1100}
      destroyOnHidden
      title={
        <span>
          <CodeOutlined style={{ marginRight: 8 }} />
          Logic wizard
        </span>
      }
      footer={
        <div className="flex justify-between items-center w-full">
          <span className="text-xs text-gray-500">
            {canApply
              ? "Ready to apply"
              : "Complete every condition to enable Apply"}
          </span>
          <div className="flex gap-2">
            <DataQButton type="secondary" onClick={onClose}>
              Cancel
            </DataQButton>
            <Tooltip
              title={
                canApply
                  ? ""
                  : "Fill in every question, operator, and value first."
              }
            >
              <DataQButton
                type="primary"
                onClick={handleApply}
                isDisabled={!canApply}
              >
                Apply
              </DataQButton>
            </Tooltip>
          </div>
        </div>
      }
    >
      {parseErrors.length > 0 && (
        <Alert
          showIcon
          type="warning"
          className="mb-3"
          message="We couldn't fully parse this condition"
          description={
            <ul className="list-disc pl-5 text-xs mb-0">
              {parseErrors.slice(0, 5).map((e, i) => (
                <li key={i}>{e}</li>
              ))}
              {parseErrors.length > 5 && (
                <li>…and {parseErrors.length - 5} more</li>
              )}
            </ul>
          }
        />
      )}

      <div className="text-sm font-medium mb-2">If this is true…</div>
      <ConditionGroup
        node={ast.kind === "group" ? ast : makeRootGroup(ast)}
        labels={labels}
        questionInfo={questionInfo}
        listMap={listMap}
        onChange={(n) => setAst(n)}
      />

      <BodyEditor
        action={bodyAction}
        onChange={setBodyAction}
        labels={labels}
      />

      <div className="mt-4 p-3 bg-gray-900 text-gray-100 rounded font-mono text-sm whitespace-pre-wrap break-words">
        {preview}
      </div>
      <div className="mt-1 text-xs text-gray-500">
        This is exactly what gets written to the editor.
      </div>
    </Modal>
    </ConfigProvider>
  )
}

function makeRootGroup(initialChild?: ASTNode): GroupNode {
  return {
    kind: "group",
    op: "AND",
    children: [initialChild ?? placeholder],
  }
}

function wrapInRoot(node: ASTNode): GroupNode {
  if (node.kind === "group") return node
  return { kind: "group", op: "AND", children: [node] }
}

interface InitialState {
  ast: ASTNode
  bodyAction: BodyAction
  parseErrors: string[]
}

/**
 * Derive the wizard's initial AST + body action + parse errors from the
 * `initialIfText` prop. Pure — called once per mount via lazy useState.
 *
 * Each `useState(() => computeInitialState(...))` re-runs this function;
 * that's three calls at mount time, but each is cheap (parseIfLine on a
 * single line plus parseBodyLines on the rest), so we don't bother
 * memoising.
 */
function computeInitialState(
  initialIfText: string | null | undefined,
): InitialState {
  if (!initialIfText || !initialIfText.trim()) {
    return {
      ast: makeRootGroup(),
      bodyAction: DEFAULT_BODY_ACTION,
      parseErrors: [],
    }
  }
  const allLines = initialIfText.split("\n")
  const ifResult = parseIfLine(allLines[0] ?? "")
  const bodyResult = parseBodyLines(allLines.slice(1))
  return {
    ast: wrapInRoot(ifResult.ast),
    bodyAction: bodyResult.action,
    parseErrors: ifResult.errors,
  }
}

export default LogicWizard
